import React, { useEffect, useRef } from 'react';
import {
  GridLoadingOverlayVariant,
  DataGrid as MUIDataGrid,
  DataGridProps as MUIDataGridProps,
  useGridApiRef,
} from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .no-rows-primary': {
    fill: '#3D4751',
    ...theme.applyStyles('light', {
      fill: '#AEB8C2',
    }),
  },
  '& .no-rows-secondary': {
    fill: '#1D2126',
    ...theme.applyStyles('light', {
      fill: '#E8EAED',
    }),
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <Typography variant="body2" sx={{ mt: 2 }}>
        No results
      </Typography>
    </StyledGridOverlay>
  );
}

interface IProps {
  isLoading?: boolean;
  onNextPage: () => void;
  threshold?: number;
}

export const useTableScrollInfinitie = ({
  onNextPage,
  isLoading,
  threshold = 0,
}: IProps) => {
  const gridApiRef = useGridApiRef();
  const scrollMonitor = useRef<any>(null);
  // Store previous scroll position
  const prevScrollPosition = useRef({ top: 0, left: 0 });

  useEffect(() => {
    if (gridApiRef.current?.instanceId) {
      // Obtenemos el elementScroll
      const elementScroll =
        gridApiRef.current.rootElementRef.current?.children[0].children[1];

      // Suscripción al evento scrollPositionChange si no está cargando datos
      if (!isLoading && elementScroll) {
        scrollMonitor.current = gridApiRef.current.subscribeEvent(
          'scrollPositionChange',
          (event) => {
            if (
              event.top !== prevScrollPosition.current.top &&
              event.left === prevScrollPosition.current.left
            ) {
              const maxScrollTop =
                elementScroll.scrollHeight - elementScroll.clientHeight;
              const scrollThreshold = maxScrollTop * (1 - threshold / 100);

              if (event.top >= scrollThreshold) {
                onNextPage();
              }
            }
            prevScrollPosition.current = { top: event.top, left: event.left };
          }
        );
      }
    }

    // Cleanup al desmontar el componente o cambiar dependencias
    return () => {
      if (scrollMonitor.current) {
        scrollMonitor.current();
      }
    };
  }, [gridApiRef, isLoading, onNextPage, threshold]);

  return { gridApiRef };
};

interface DataGridProps extends MUIDataGridProps {
  onNextPage?: () => void;
  loadingVariant?: GridLoadingOverlayVariant;
}

export const DataGrid = ({
  onNextPage,
  loadingVariant,
  ...props
}: DataGridProps) => {
  const { gridApiRef } = useTableScrollInfinitie({
    onNextPage: () => (onNextPage ? onNextPage() : console.log('Next Page')),
    isLoading: props.loading,
    threshold: 10,
  });

  const loading =
    loadingVariant ||
    (props.rows && props.rows.length > 0 ? 'linear-progress' : 'skeleton');

  return (
    <MUIDataGrid
      {...props}
      apiRef={gridApiRef}
      slotProps={{
        loadingOverlay: {
          variant: loading,
          noRowsVariant: loading,
        },
      }}
      slots={{
        pagination: null,
        noRowsOverlay: CustomNoRowsOverlay,
      }}
      density="standard"
      disableColumnMenu
      hideFooter
      rowCount={props.rowCount ?? props.rows?.length}
      paginationMode="server"
      sortingMode="server"
      filterMode="server"
    />
  );
};
